import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Container, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Footer from '../home/Footer';
import './Login.css';
import CustomFormAction from '../customComponents/CustomFormAction';
import CustomButton from '../customComponents/CustomButton';
import AuthService from '../services/AuthService';
import { useSelector } from 'react-redux';
import { selectAuth } from '../stateManagement/authSlice';
import { ResetPasswordConfirmModel, ResetPasswordRequestModel } from "@ontime/types";

const requestSchema = z.object({
  emailAddress: z.string().email("Invalid email format"),
});

const confirmSchema = z.object({
  emailAddress: z.string().email("Invalid email format"),
  confirmationCode: z.string().min(6, "Code must be at least 6 characters"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
});



const ResetPassword: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);

  const [step, setStep] = useState<"request" | "confirm">("request");
  const [loading, setLoading] = useState(false);

const {
  register,
  handleSubmit,
  formState: { errors },
  setValue,
} = useForm<any>({
  resolver: zodResolver(step === "request" ? requestSchema : confirmSchema),
});

  useEffect(() => {
    if (auth && auth.email) {
      setValue("emailAddress", auth.email);
    }
  }, [auth, setValue]);

  const handleRequest = async (data: ResetPasswordRequestModel) => {
    setLoading(true);
    try {
      const response = await AuthService.forgotPassword(data.emailAddress);
      if (response.success) {
        enqueueSnackbar('Reset code sent to your email.', { variant: 'success' });
        setStep("confirm");
      }
    } catch (error) {
      let errorMessage = typeof error === 'string' ? error : 'Reset request failed. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (data: ResetPasswordConfirmModel) => {
    setLoading(true);
    try {
      const response = await AuthService.confirmPassword(data.emailAddress, data.confirmationCode, data.newPassword);
      if (response.success) {
        enqueueSnackbar('Password reset successful! You can now log in.', { variant: 'success' });
        navigate('/login');
      }
    } catch (error) {
      let errorMessage = typeof error === 'string' ? error : 'Password reset failed. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <Container component="main" maxWidth="xs">
        <Paper className="register-paper" elevation={3}>
          <Box className="logo-box" >
            <img onClick={() => navigate('/home')} style={{ cursor: 'pointer' }} src="logo.png" alt="Logo" className="register-logo" />
          </Box>
          <Typography component="h1" variant="h5" className="register-title">
            Reset Password
          </Typography>

          <form onSubmit={handleSubmit(step === "request" ? handleRequest : handleConfirm)} noValidate>
            <Box mt={2}>
              <TextField
                variant="outlined"
                fullWidth
                label="Email"
                InputLabelProps={{ shrink: true }}
                {...register('emailAddress')}
                error={!!errors.emailAddress}
                // helperText={errors.emailAddress?.message}
                disabled={step === "confirm"}
              />
            </Box>

            {step === "confirm" && (
              <>
                <Box mt={2}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Confirmation Code"
                    InputLabelProps={{ shrink: true }}
                    {...register('confirmationCode')}
                    error={!!errors.confirmationCode}
                    // helperText={errors.confirmationCode?.message}
                  />
                </Box>
                <Box mt={2}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="New Password"
                    type="password"
                    InputLabelProps={{ shrink: true }}
                    {...register('newPassword')}
                    error={!!errors.newPassword}
                    // helperText={errors.newPassword?.message}
                  />
                </Box>
              </>
            )}

            <CustomFormAction>
              <CustomButton
                lable={loading ? (step === "request" ? "Sending..." : "Resetting...") : (step === "request" ? "Send Reset Code" : "Confirm New Password")}
                type="submit"
                disabled={loading}
                infoContent={step === "request" ? "Request password reset" : "Confirm password reset"}
                size="medium"
              />
            </CustomFormAction>

            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                Remembered your password?{" "}
                <a href="/login" style={{ textDecoration: "none", color: "#1976d2" }}>Log in here</a>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
      <Box sx={{ height: '65px' }} />
      <div className="footer-register-container" >
        <Footer />
      </div>
    </div>
  );
};

export default ResetPassword;